import { zodResolver } from '@hookform/resolvers/zod'
import { ErrorOutline, SettingsOutlined } from '@mui/icons-material'
import { Box, Divider, Drawer, Stack, Tab, Tabs, TextField } from '@mui/material'
import type { TFunction } from 'i18next'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { FloatTextFieldController } from '@/components/inputs/FloatTextFieldController'
import { IntegerTextFieldController } from '@/components/inputs/IntegerTextFieldController'
import TabPanel from '@/components/layouts/TabPanel'
import { ConfigField } from '@/features/controller/configuration/components/drawers/ConfigField'
import { DrawerActions } from '@/features/controller/configuration/components/drawers/DrawerActions'
import { DrawerDescription } from '@/features/controller/configuration/components/drawers/DrawerDescription'
import { DrawerTitle } from '@/features/controller/configuration/components/drawers/DrawerTitle'
import { IntegerConfigField } from '@/features/controller/configuration/components/drawers/IntegerConfigField'
import { RegisterConfigField } from '@/features/controller/configuration/components/drawers/RegisterConfigField'
import { ToggleButtonGroupEnumConfigField } from '@/features/controller/configuration/components/drawers/ToggleButtonGroupEnumConfigField'
import {
  CONFIG_FIELD_NUMBER_LONG_SX,
  CONFIG_FIELD_NUMBER_MEDIUM_SX,
} from '@/features/controller/configuration/constants'
import type {
  DeviceConnectionNode,
  LegacyModbusPowerMeterNode,
} from '@/features/controller/configuration/models/controllerConfigTree'
import { DeviceConnectionNodeType } from '@/features/controller/configuration/models/controllerConfigTree'
import { threePhaseActivePowerRegisterAddresses } from '@/features/controller/configuration/models/raspiProperties/modbus'
import type { SerialPort, SerialPortConfig } from '@/features/controller/configuration/models/raspiProperties/types'
import {
  ModbusDataType,
  ModbusPowerRegisterVariant,
  ModbusRegisterType,
  WordOrder,
} from '@/features/controller/configuration/models/raspiProperties/types'
import { getLegacyModbusMeterSchema } from '@/features/controller/configuration/schemas'
import type { ConfigDrawerProps, LegacyModbusMeterSchemaType } from '@/features/controller/configuration/types'
import { renderRegisterAddress } from '@/features/controller/configuration/utils/configForms'
import { describeSerialPort } from '@/features/controller/configuration/utils/serialPorts'

const COMMON_POWER_SCALES = [1, 1000]

function describeSerialEndpoint({ baudRate }: SerialPortConfig, serialPort: SerialPort, t: TFunction) {
  const rate = t('controllers.configuration.device_connections.serial.baud_rate_summary', { value: baudRate })
  return `${describeSerialPort(serialPort, t)} @ ${rate}`
}

/**
 * The parent connection, named and pointed the way its row in the tree names and points it. Every
 * meter has one, so this reads the same whether the endpoint is the meter's own inline host or a
 * connection it shares with other devices.
 */
function describeParentConnection(connectionNode: DeviceConnectionNode, t: TFunction) {
  switch (connectionNode.type) {
    case DeviceConnectionNodeType.LegacySerial: {
      const { portConfig, serialPort } = connectionNode.config.legacySerialPortConfig
      return `${connectionNode.label} · ${describeSerialEndpoint(portConfig, serialPort, t)}`
    }
    case DeviceConnectionNodeType.LegacyModbusTcp: {
      const { host, port } = connectionNode.config.tcpTarget
      return `${connectionNode.label} · ${host}:${port}`
    }
    case DeviceConnectionNodeType.NamedModbusTcp: {
      const { address, name, port } = connectionNode.config.namedClient
      return `${name} · ${address}:${port}`
    }
    case DeviceConnectionNodeType.NamedModbusSerial: {
      const { name, portConfig, serialPort } = connectionNode.config.namedClient
      return `${name} · ${describeSerialEndpoint(portConfig, serialPort, t)}`
    }
  }
}

type ModbusMeterSettingsDrawerProps = ConfigDrawerProps<LegacyModbusPowerMeterNode> & {
  connectionNode: DeviceConnectionNode
}

export function ModbusMeterSettingsDrawer({
  config,
  connectionNode,
  onClose,
  onConfigUpdated,
  onFinishedClosing,
  open,
}: Readonly<ModbusMeterSettingsDrawerProps>) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(0)

  const { errors, number, ...configPart } = config.config
  // The endpoint is configured on the connection row, never here, so it is shown read-only
  const parentConnection = describeParentConnection(connectionNode, t)

  const form = useForm<LegacyModbusMeterSchemaType>({
    defaultValues: configPart,
    mode: 'onBlur',
    resolver: zodResolver(getLegacyModbusMeterSchema()),
  })

  function onInvalid() {
    if (generalTabError) {
      setActiveTab(0)
    } else if (powerRegistersTabError) {
      setActiveTab(1)
    } else if (otherRegistersTabError) {
      setActiveTab(2)
    }
  }

  useEffect(() => {
    // Trigger callback when there are errors
    if (Object.keys(form.formState.errors).length !== 0) {
      onInvalid()
    }
  }, [JSON.stringify(form.formState.errors), form.formState.submitCount])

  function getCurrentValues(updatedConfig: LegacyModbusMeterSchemaType) {
    return {
      ...configPart,
      // General tab
      deviceNumber: updatedConfig.deviceNumber,
      unitId: updatedConfig.unitId,
      registerAddressOffset: updatedConfig.registerAddressOffset ?? 0,
      wordOrder: updatedConfig.wordOrder,
      powerMeasurementSkipPeriodInMilliseconds: updatedConfig.powerMeasurementSkipPeriodInMilliseconds,
      // Power registers
      powerRegistersDataType: updatedConfig.powerRegistersDataType,
      activePowerRegisters: updatedConfig.activePowerRegisters,
      activePowerScale: updatedConfig.activePowerScale ?? 1,
      reactivePowerRegister: updatedConfig.reactivePowerRegister,
      reactivePowerScale: updatedConfig.reactivePowerScale ?? 1,
      // Other registers
      watchdogRegister: updatedConfig.watchdogRegister,
      meterErrorRegister: updatedConfig.meterErrorRegister,
      systemTimestampRegister: updatedConfig.systemTimestampRegister,
      dataTimestampRegister: updatedConfig.dataTimestampRegister,
    }
  }

  function onSave(updatedConfig: LegacyModbusMeterSchemaType) {
    onConfigUpdated({
      ...config,
      config: {
        ...getCurrentValues(updatedConfig),
        errors,
        number,
      },
    })
  }

  const TAB_FIELDS = {
    general: [
      'deviceNumber',
      'unitId',
      'registerAddressOffset',
      'wordOrder',
      'powerMeasurementSkipPeriodInMilliseconds',
    ],
    powerRegisters: [
      'powerRegistersDataType',
      'activePowerRegisters.variant',
      'activePowerRegisters.address',
      'activePowerRegisters.registers',
      'activePowerScale',
      'reactivePowerRegister.address',
      'reactivePowerRegister.registers',
      'reactivePowerScale',
    ],
    otherRegisters: [
      'watchdogRegister.address',
      'watchdogRegister.registers',
      'meterErrorRegister.address',
      'meterErrorRegister.registers',
      'systemTimestampRegister.address',
      'systemTimestampRegister.registers',
      'dataTimestampRegister.address',
      'dataTimestampRegister.registers',
    ],
  } as const

  const hasFieldErrors = (fields: readonly string[]): boolean => {
    return fields.some((field) => form.getFieldState(field as any).error)
  }
  const generalTabError = hasFieldErrors(TAB_FIELDS.general)
  const powerRegistersTabError = hasFieldErrors(TAB_FIELDS.powerRegisters)
  const otherRegistersTabError = hasFieldErrors(TAB_FIELDS.otherRegisters)

  const commonTabProps = {
    iconPosition: 'start',
    style: { minHeight: 0 },
  } as const

  const currentValues = getCurrentValues(form.watch())
  const threePhaseVariant = currentValues.activePowerRegisters?.variant === ModbusPowerRegisterVariant.threePhase

  const { phase2, phase3 } = threePhaseActivePowerRegisterAddresses(
    currentValues.activePowerRegisters?.address ?? Number.NaN,
    currentValues.powerRegistersDataType,
  )

  const hasChanges = JSON.stringify(currentValues) !== JSON.stringify(configPart)

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 580 },
        },
      }}
      onClose={onClose}
      onTransitionExited={onFinishedClosing}
    >
      <FormProvider {...form}>
        <datalist id="common_power_scales">
          {COMMON_POWER_SCALES.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>

        <DrawerTitle
          title={t('controllers.configuration.power_meters.modbus.dialog.title', { meterNumber: number })}
          onClose={onClose}
        />

        <Divider />

        <Box sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(Number(value))}>
            <Tab
              {...commonTabProps}
              icon={generalTabError ? <ErrorOutline color="error" /> : <SettingsOutlined />}
              label={t('controllers.configuration.power_meters.modbus.dialog.tabs_labels.general')}
              value={0}
            />
            <Tab
              {...commonTabProps}
              icon={powerRegistersTabError ? <ErrorOutline color="error" /> : <SettingsOutlined />}
              label={t('controllers.configuration.power_meters.modbus.dialog.tabs_labels.power_registers')}
              value={1}
            />
            <Tab
              {...commonTabProps}
              icon={otherRegistersTabError ? <ErrorOutline color="error" /> : <SettingsOutlined />}
              label={t('controllers.configuration.power_meters.modbus.dialog.tabs_labels.other_registers')}
              value={2}
            />
          </Tabs>
        </Box>

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          {/* General settings */}
          <TabPanel index={0} value={activeTab}>
            <DrawerDescription
              description={t('controllers.configuration.power_meters.modbus.dialog.tabs.general.description')}
            />

            <Stack direction="column" padding={2} spacing={2}>
              <ConfigField
                helpTooltip={t('controllers.configuration.power_meters.modbus.dialog.tabs.general.connection_hint')}
                label={t('controllers.configuration.power_meters.modbus.dialog.tabs.general.connection')}
              >
                <CustomTypography variant="body1">{parentConnection}</CustomTypography>
              </ConfigField>
              <Divider />
              <IntegerConfigField
                helpTooltip={t('controllers.configuration.power_meters.modbus.dialog.tabs.general.device_number_hint')}
                label={t('controllers.configuration.power_meters.modbus.dialog.tabs.general.device_number')}
                name="deviceNumber"
                textFieldProps={{
                  slotProps: { htmlInput: { min: 1 } },
                }}
              />
              <IntegerConfigField
                helpTooltip={t('controllers.configuration.power_meters.modbus.dialog.tabs.general.unit_id_hint')}
                label={t('controllers.configuration.power_meters.modbus.dialog.tabs.general.unit_id')}
                name="unitId"
                textFieldProps={{
                  slotProps: { htmlInput: { min: 1 } },
                }}
              />

              <Divider />

              <IntegerConfigField
                label={t('controllers.configuration.power_meters.modbus.dialog.tabs.general.register_address_offset')}
                name="registerAddressOffset"
              />
              <ToggleButtonGroupEnumConfigField
                enumLabels={{
                  LITTLE_ENDIAN: t(
                    'controllers.configuration.power_meters.modbus.dialog.tabs.general.word_order.little_endian',
                  ),
                  BIG_ENDIAN: t(
                    'controllers.configuration.power_meters.modbus.dialog.tabs.general.word_order.big_endian',
                  ),
                }}
                enumType={WordOrder}
                helpTooltip={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.general.register_word_order_hint',
                )}
                label={t('controllers.configuration.power_meters.modbus.dialog.tabs.general.register_word_order')}
                name="wordOrder"
              />

              <Divider />

              <IntegerConfigField
                label={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.general.period_between_measurements',
                )}
                name="powerMeasurementSkipPeriodInMilliseconds"
                textFieldProps={{
                  slotProps: { htmlInput: { min: 0, step: 100 } },
                }}
                unit={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.general.period_between_measurements_unit',
                )}
              />
            </Stack>
          </TabPanel>

          {/* Power registers */}
          <TabPanel index={1} value={activeTab}>
            <DrawerDescription
              description={t('controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.description')}
            />

            <Stack direction="column" padding={2} spacing={2}>
              <ToggleButtonGroupEnumConfigField
                enumType={ModbusDataType}
                label={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.power_registers_data_type',
                )}
                name="powerRegistersDataType"
              />

              <Divider />

              <Stack flexDirection="row">
                <ToggleButtonGroupEnumConfigField
                  enumLabels={{
                    threePhase: t(
                      'controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.variant.three_phase',
                    ),
                    total: t('controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.variant.total'),
                  }}
                  enumType={ModbusPowerRegisterVariant}
                  flex={5}
                  label={t(
                    'controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.active_power_registers',
                  )}
                  name="activePowerRegisters.variant"
                />
                <ConfigField
                  flex={1}
                  label={t('controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.data_type')}
                >
                  <CustomTypography alignContent="center" height={42}>
                    {form.watch().powerRegistersDataType}
                  </CustomTypography>
                </ConfigField>
              </Stack>
              <Stack flexDirection="row">
                <ConfigField
                  flex={2}
                  label={t(
                    'controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.active_power_register_addresses',
                  )}
                >
                  <Stack flexDirection="row" gap={2}>
                    <IntegerTextFieldController
                      name="activePowerRegisters.address"
                      textFieldProps={{ sx: CONFIG_FIELD_NUMBER_MEDIUM_SX, type: 'number' }}
                    />
                    {threePhaseVariant && (
                      <>
                        <TextField
                          disabled={true}
                          sx={CONFIG_FIELD_NUMBER_MEDIUM_SX}
                          value={renderRegisterAddress(phase2)}
                        />
                        <TextField
                          disabled={true}
                          sx={CONFIG_FIELD_NUMBER_MEDIUM_SX}
                          value={renderRegisterAddress(phase3)}
                        />
                      </>
                    )}
                  </Stack>
                </ConfigField>
                <ToggleButtonGroupEnumConfigField
                  enumLabels={{
                    holding: t(
                      'controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.register_type.holding',
                    ),
                    input: t(
                      'controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.register_type.input',
                    ),
                  }}
                  enumType={ModbusRegisterType}
                  flex={1}
                  label={t('controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.registers')}
                  name="activePowerRegisters.registers"
                  toggleButtonGroupControllerProps={{ sx: { height: '42px' } }}
                />
              </Stack>
              <ConfigField
                label={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.active_power_scale',
                )}
              >
                <FloatTextFieldController
                  name="activePowerScale"
                  textFieldProps={{
                    slotProps: { htmlInput: { list: 'common_power_scales' } },
                    sx: CONFIG_FIELD_NUMBER_LONG_SX,
                  }}
                />
              </ConfigField>

              <Divider />

              <RegisterConfigField
                addressLabel={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.reactive_power_register_address',
                )}
                checkboxLabel={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.reactive_power_register',
                )}
                dataType={form.watch().powerRegistersDataType}
                name="reactivePowerRegister"
              />
              <ConfigField
                label={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.power_registers.reactive_power_scale',
                )}
              >
                <FloatTextFieldController
                  name="reactivePowerScale"
                  textFieldProps={{
                    slotProps: { htmlInput: { list: 'common_power_scales' } },
                    sx: CONFIG_FIELD_NUMBER_LONG_SX,
                  }}
                />
              </ConfigField>
            </Stack>
          </TabPanel>

          {/* Other registers */}
          <TabPanel index={2} value={activeTab}>
            <DrawerDescription
              description={t('controllers.configuration.power_meters.modbus.dialog.tabs.other_registers.description')}
            />

            <Stack direction="column" padding={2} spacing={2}>
              <RegisterConfigField
                addressLabel={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.other_registers.watchdog_register_address',
                )}
                checkboxLabel={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.other_registers.watchdog_register',
                )}
                dataType="INT16"
                name="watchdogRegister"
              />
              <RegisterConfigField
                addressLabel={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.other_registers.meter_error_register_address',
                )}
                checkboxLabel={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.other_registers.meter_error_register',
                )}
                dataType="INT16"
                name="meterErrorRegister"
              />
              <RegisterConfigField
                addressLabel={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.other_registers.system_timestamp_register_address',
                )}
                checkboxLabel={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.other_registers.system_timestamp_register',
                )}
                dataType="INT64"
                name="systemTimestampRegister"
              />
              <RegisterConfigField
                addressLabel={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.other_registers.data_timestamp_register_address',
                )}
                checkboxLabel={t(
                  'controllers.configuration.power_meters.modbus.dialog.tabs.other_registers.data_timestamp_register',
                )}
                dataType="INT64"
                name="dataTimestampRegister"
              />
            </Stack>
          </TabPanel>
        </Box>

        <Divider />

        {/* Footer actions */}
        <DrawerActions hasChanges={hasChanges} onClose={onClose} onSave={onSave} />
      </FormProvider>
    </Drawer>
  )
}
